import { useEffect, useMemo, useState } from "react";

import { StoreLocatorCardV2 } from "@locators/components/shared/components/StoreLocatorCardV2";
import { useLocatorTranslations } from "@locators/components/shared/hooks/useLocatorTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import { LoadMore } from "@jti/ui/components/LoadMore";

import { useStoreLocatorContext, useStoreTrackingEvent } from "../../hooks";
import { getStoreDetailsPageUrl } from "../../utils";

import styles from "./StoreLocatorMobileList.module.scss";

const DEFAULT_PAGE_SIZE = 8;
const DEFAULT_PAGE = 1;

export const StoreLocatorMobileList = ({ pageSize = DEFAULT_PAGE_SIZE }: { pageSize?: number }) => {
  const { __ } = useLocatorTranslations();
  const publishStoreTrackingEvent = useStoreTrackingEvent();
  const { isSeoFriendlyStoreLocatorEnabled } = useConfig();
  const [page, setPage] = useState(DEFAULT_PAGE);

  const {
    flotiqStores,
    storeDetailsPage,
    isOpeningHoursEnabled,
    isOpeningHoursBreaksEnabled,
    isAvailableDevicesEnabled,
    isAvailableSticksEnabled,
    isMultiBrandSticksEnabled,
    defaultZoom,
    isGeneralAnnouncementEnabled,
    isAvailableServicesEnabled,
    isStoreTypeEnabled,
    isVendingMachineEnabled,
    isVendingMachineTipEnabled,
    isNearbyShopsEnabled,
    isAsianAddressFormatEnabled,
    translationPrefix,
  } = useStoreLocatorContext();

  useEffect(() => {
    setPage(DEFAULT_PAGE);
  }, [flotiqStores]);

  const stores = useMemo(() => {
    return flotiqStores.slice(0, pageSize * page);
  }, [flotiqStores, page, pageSize]);

  const hasMoreStores = stores.length < flotiqStores.length;

  return (
    <div className={styles.wrapper}>
      <ul className={styles.list}>
        {stores.map((store) => {
          const storeDetailsPageUrl = getStoreDetailsPageUrl({
            storeDetailsPage,
            store,
            defaultZoom,
            isOpeningHoursEnabled,
            isOpeningHoursBreaksEnabled,
            isAvailableDevicesEnabled,
            isAvailableSticksEnabled,
            isMultiBrandSticksEnabled,
            isGeneralAnnouncementEnabled,
            isAvailableServicesEnabled,
            isStoreTypeEnabled,
            isVendingMachineEnabled,
            isVendingMachineTipEnabled,
            isNearbyShopsEnabled,
            isAsianAddressFormatEnabled,
            isSeoFriendlyUrlsEnabled: isSeoFriendlyStoreLocatorEnabled,
          });
          const resolvedCtaUrl = store.CtaHref || storeDetailsPageUrl;
          return (
            <li key={store.id}>
              <StoreLocatorCardV2
                store={store}
                storeDetailsPageUrl={resolvedCtaUrl}
                isListView={true}
                ctaLabel={store?.CtaLabel}
                isAddressLabelVisible={translationPrefix !== "horeca"}
                onSelectCard={publishStoreTrackingEvent}
              />
            </li>
          );
        })}
      </ul>
      {hasMoreStores && (
        <LoadMore
          className={styles.laodMore}
          onClick={() => {
            setPage((page) => page + 1);
          }}
          label={__("storelocator.show_more")}
        />
      )}
    </div>
  );
};
